import * as rax from 'retry-axios';
import axios, { AxiosInstance, AxiosRequestConfig, CanceledError } from 'axios';
import authModule from './AuthModule';
import { isInteractionRequired, timedOut } from './authRejection';

declare module 'axios' {
    interface AxiosRequestConfig {
        /** false: a try that ran out of time (config.timeout) is not asked again: the server is
         * still working on it. A connection that failed at once is. */
        retryTimedOut?: boolean;
        /** Asked before each retry: false ends the call with the failure it had (the token calls, once
         * the sign-in they were made for is gone and their answer would be dropped). */
        stillWanted?: () => boolean;
    }
}

export const apiHeaders = {
    key: {
        xDocubirdAuth: "x-docubird-auth"
    },
    value: {
        xDocubirdAuth: "]c4&ql>@P3flVbPM#61bX}d3q<e=$f"
    }
}

export const localApiInstance = axios.create({
    baseURL: process.env.API_URL,
    headers: {
        [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
    }
});

if (navigator.userAgent.indexOf("Trident") !== -1 || navigator.userAgent.indexOf("Edge") !== -1) {
    // No AbortController here: signed out, the request is cancelled by throwing. It went out anyway,
    // without a JWT (audit 2026-10-27).
    localApiInstance.interceptors.request.use(async (config) => {
        const jwtToken = await authModule.getDocubirdToken();
        if (jwtToken) {
            config.headers["Authorization"] = `Bearer ${jwtToken}`;
        } else {
            authModule.signOutDetected();
            throw new CanceledError("Signed out");
        }
        return {
            ...config
        };
    });
}
else {
    localApiInstance.interceptors.request.use(async (config) => {
        const controller = new AbortController();
        const jwtToken = await authModule.getDocubirdToken();
        if (jwtToken) {
            config.headers["Authorization"] = `Bearer ${jwtToken}`;
        } else {
            controller.abort();
            authModule.signOutDetected();
        }
        return {
            ...config,
            signal: controller.signal
        };
    });
}

// Retries go out through the global axios (no raxConfig.instance, below): one for a call no longer
// wanted isn't sent, also when that changed during its backoff, after retry-axios scheduled it.
// The call fails as cancelled, with its config.
axios.interceptors.request.use(config => {
    if (config.stillWanted?.() === false) {
        throw Object.assign(new CanceledError("No longer wanted"), { config });
    }
    return config;
});

// Attach retry-axios to the global axios and localApiInstance
rax.attach();
rax.attach(localApiInstance);

const retryCodes = [[100, 199], [408, 408], [429, 429], [500, 599]];
const altRetryCodes = [[100, 199], [408, 408], [429, 429], [501, 599]];
const retryAmount = 5;

// A refused sign-in is never asked again: five more tries only added 15 s before the sign-in showed.
// Nor is a try that ran out of time for a call that says so (retryTimedOut: false, the token calls):
// each was sent twice more while the server still worked on it (audit 2026-10-27). Nor a call no
// longer wanted (stillWanted): in an outage a token call's retries went on after the sign-out. A try
// the browser aborted is asked again, as before (timedOut).
const shouldRetry = (error: any): boolean =>
    !isInteractionRequired(error)
    && !(error?.config?.retryTimedOut === false && timedOut(error))
    && error?.config?.stillWanted?.() !== false
    && rax.shouldRetryRequest(error);

// No raxConfig.instance: retry-axios asks again through the global axios, past localApiInstance's
// interceptor, with the first try's JWT, the account the request was made for. Through the instance,
// a retry after an account switch carried the new account's JWT, and with the API down each retry
// renewed the JWT again (2026-10-27).
export const axiosRetryGet = async (
    url: string,
    config?: AxiosRequestConfig,
    axiosInstance: AxiosInstance | typeof axios = axios
) => {

    const res = await axiosInstance.get(url, {
        ...config,
        raxConfig: {
            retry: retryAmount,
            httpMethodsToRetry: ["GET"],
            statusCodesToRetry: retryCodes,
            backoffType: "linear",
            shouldRetry
        }
    });

    return res;
}

export const axiosRetryPost = async (
    url: string,
    data: any = null,
    config?: AxiosRequestConfig,
    retryInternalServerError: boolean = true,
    axiosInstance: AxiosInstance | typeof axios = axios
) => {

    const res = await axiosInstance.post(url, data, {
        ...config,
        raxConfig: {
            retry: retryAmount,
            httpMethodsToRetry: ["POST"],
            statusCodesToRetry: retryInternalServerError ? retryCodes : altRetryCodes,
            backoffType: "linear",
            shouldRetry
        }
    });

    return res;
}

export const axiosRetryPut = async (url: string, data: any = null, axiosInstance: AxiosInstance | typeof axios = axios) => {

    const res = await axiosInstance.put(url, data, {
        raxConfig: {
            retry: retryAmount,
            httpMethodsToRetry: ["PUT"],
            statusCodesToRetry: retryCodes,
            backoffType: "linear",
            shouldRetry
        }
    });

    return res;
}

export const axiosRetryDelete = async (url: string) => {

    const res = await axios.delete(url, {
        raxConfig: {
            retry: retryAmount,
            httpMethodsToRetry: ["DELETE"],
            statusCodesToRetry: retryCodes,
            backoffType: "linear",
            shouldRetry
        }
    });

    return res;
}

export const axiosRetryPatch = async (url: string, data: any = null, config?: AxiosRequestConfig) => {

    const res = await axios.patch(url, data, {
        ...config,
        raxConfig: {
            retry: retryAmount,
            httpMethodsToRetry: ["PATCH"],
            statusCodesToRetry: retryCodes,
            backoffType: "linear",
            shouldRetry
        }
    });

    return res;
}